import type { FeedPost } from "@gooner/shared";

/**
 * WHERE THE FEED WAS LEFT — remembered per history entry, so Back puts you where you were.
 *
 * The Feed keeps its posts, its "Load more" cursor and (obviously) the scroll offset in
 * component state. Menu -> Browse unmounts it, and the browser Back that returns remounts
 * it from nothing: one fresh page of 20 posts under a "Loading the Feed…" line, a document
 * too short for the browser's own scroll restoration to land anywhere, and the reader back
 * at the first post (Casey 2026-10-16 midday). So the unmount saves a snapshot and the
 * remount starts from it.
 *
 * KEYED BY `location.key`, the router's id for a HISTORY ENTRY: Back returns to the same
 * entry (same key, snapshot found); a fresh visit through the Menu or a link is a new entry
 * (new key, nothing found, a normal first page). Module memory, not storage: it survives
 * in-app navigation, which is the whole case, and a reload or a new tab starts clean,
 * which is what a reload should do. Bounded, and old after a while: a snapshot of posts
 * from this morning is not what Back to the Feed should show this afternoon.
 */
export interface FeedSnapshot {
  posts: FeedPost[];
  nextBefore: number | undefined;
  scrollY: number;
}

interface Stored extends FeedSnapshot {
  at: number;
}

export const FEED_SNAPSHOT_TTL_MS = 10 * 60_000;
export const FEED_SNAPSHOT_MAX = 8;

/** One id per page load (module evaluation). Snapshots are memory only and die with the page;
 *  this makes the key say so too, so an entry of one load can never match another's. */
const LOAD_ID = Math.random().toString(36).slice(2, 10);

const store = new Map<string, Stored>();

export function saveFeedSnapshot(key: string, snap: FeedSnapshot, now = Date.now()): void {
  // An empty feed has nothing to put back, and a snapshot of "no posts" would only hide
  // the load that might now find some.
  if (snap.posts.length === 0) return;
  store.delete(key);
  store.set(key, { ...snap, scrollY: Math.max(0, Math.round(snap.scrollY) || 0), at: now });
  while (store.size > FEED_SNAPSHOT_MAX) {
    const oldest = store.keys().next().value;
    if (oldest === undefined) break;
    store.delete(oldest);
  }
}

export function readFeedSnapshot(key: string, now = Date.now()): FeedSnapshot | null {
  const s = store.get(key);
  if (!s) return null;
  if (now - s.at > FEED_SNAPSHOT_TTL_MS) {
    store.delete(key);
    return null;
  }
  return { posts: s.posts, nextBefore: s.nextBefore, scrollY: s.scrollY };
}

export function clearFeedSnapshots(): void {
  store.clear();
}

/**
 * The snapshot key for one history entry. `location.key` alone is not enough: the router
 * gives the entry a page load starts on (a typed /feed, a reload) the constant key
 * "default", so every such entry would share one snapshot. The browser's per-entry
 * `history.state.idx` (set by the router) tells those apart within a session.
 */
export function feedEntryKey(locationKey: string, state?: unknown): string {
  const idx = (state as { idx?: unknown } | null | undefined)?.idx;
  return `${LOAD_ID}:${typeof idx === "number" ? `${locationKey}#${idx}` : locationKey}`;
}
